<template>
  <VueFinalModal
      content-transition="vfm-slide-left"
      :click-to-close="false"
      class="flex justify-start items-center"
      content-class="flex flex-col bg-white rounded-2xl w-[600px] h-[calc(100%-20px)] ml-[370px] 2xl:ml-[370px]"
  >
    <!-- Header -->
    <header class="w-full min-h-[50px] border-b border-gray-200 bg-[#fff8f2] rounded-t-2xl flex justify-between items-center px-4">
      <h1 class="text-[#3E546C] font-normal">Create Hadith</h1>
      <button @click="confirm" class="rounded-lg">
        <span class="pr pr-add text-[#3E546C] hover:text-[#000000]"></span>
      </button>
    </header>

    <!-- Content Section -->
    <section class="flex-grow flex flex-col p-10 gap-5">
      <!-- Hadith Title -->
      <div class="flex flex-col gap-2">
        <label for="title" class="text-sm font-medium text-gray-700">Title</label>
        <InputText id="title" v-model="hadith.title" type="text" placeholder="Enter hadith title" class="mt-1" />
      </div>

      <!-- Author ID -->
      <div class="flex flex-col gap-2">
        <label for="author" class="text-sm font-medium text-gray-700">Author</label>
        <InputText id="author" v-model="hadith.author_id" type="text" placeholder="Enter author ID" class="mt-1" />
      </div>

      <!-- Hadith Image Upload -->
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-gray-700">Hadith Image</label>
        <div
            ref="dropZoneImageRef"
            @click="triggerImageFileInput"
            class="w-full h-60 dropzone cursor-pointer border-2 border-dashed border-gray-300 rounded-md p-5 flex justify-center items-center relative"
            :class="{ 'bg-blue-100 border-blue-300': isOverImageDropZone }"
        >
          <input type="file" ref="imageFileInputRef" @change="handleImageFileChange" accept="image/*" class="hidden" />
          <img
              v-if="hadith.image"
              :src="hadith.image"
              alt="Uploaded Image"
              class="object-cover rounded-md absolute inset-0 w-full h-full"
          />
          <div
              v-if="hadith.image"
              class="rounded-md absolute inset-0 flex justify-center items-center bg-black bg-opacity-50 text-white"
          >
            Click or drop an image here to upload
          </div>
          <div v-else class="rounded-md text-center text-sm text-gray-500">
            Click or drop an image here to upload
          </div>
        </div>
      </div>

      <!-- MP3 File Upload -->
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-gray-700">Hadith MP3 File</label>
        <div
            ref="dropZoneAudioRef"
            @click="triggerAudioFileInput"
            class="w-full h-24 dropzone cursor-pointer border-2 border-dashed border-gray-300 rounded-md p-5 flex justify-center items-center relative"
            :class="{ 'bg-blue-100 border-blue-300': isOverAudioDropZone }"
        >
          <input type="file" ref="audioFileInputRef" @change="handleAudioFileChange" accept="audio/mp3" class="hidden" />
          <div v-if="hadith.audio" class="w-full">
            <!-- Audio Player -->
            <audio controls :src="hadith.audioUrl" class="w-full">
              Your browser does not support the audio element.
            </audio>
            <div
                class="absolute inset-0 flex justify-center items-center bg-black bg-opacity-50 text-white"
                @click.stop="triggerAudioFileInput"
            >
              Click or drop an MP3 file here to change
            </div>
          </div>
          <div v-else class="rounded-md text-center text-sm text-gray-500">
            Click or drop an MP3 file here to upload
          </div>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="w-full min-h-[50px] border-t border-gray-200 bg-[#fff8f2] rounded-b-2xl flex justify-end items-center px-4">
      <button
          :disabled="!hadith.title || !selectedImageFile || !selectedAudioFile"
          @click="save"
          class="bg-[#3e546c] hover:bg-[#0D2B4B] px-3 py-1 rounded-md text-white text-xs disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Save
      </button>
    </footer>
  </VueFinalModal>
</template>

<script setup>
import { ref, watch } from 'vue';
import { VueFinalModal } from 'vue-final-modal';
import InputText from 'primevue/inputtext';
import { useDropZone } from '@vueuse/core';
import { useHadithStore } from '@/store/hadiths/index.js';
import {usePagesStore} from "@/store/pages/index.js";

const emit = defineEmits(['confirm', 'created', 'close']);

// Refs for image upload
const dropZoneImageRef = ref(null);
const imageFileInputRef = ref(null);

// Refs for audio upload
const dropZoneAudioRef = ref(null);
const audioFileInputRef = ref(null);

const hadith = ref({
  title: '',
  author_id: null,
  image: null, // For storing the image preview URL
  audio: null, // For storing the audio file object
  audioUrl: null, // For storing the audio preview URL
});

const selectedImageFile = ref(null);
const selectedAudioFile = ref(null);

const hadithStore = useHadithStore();
const pagesStore = usePagesStore();

/// IMAGE UPLOAD HANDLERS ///

// DropZone setup for Image
const { isOverDropZone: isOverImageDropZone } = useDropZone(dropZoneImageRef, {
  onDrop: (files) => {
    handleImageFiles(files);
  },
  onDragEnter: () => {},
  onDragLeave: () => {},
  dataTransferTypes: ['Files'],
  preventDefault: true,
});

// Function to trigger image file input for uploads
const triggerImageFileInput = () => {
  imageFileInputRef.value.click();
};

// Handle image file input change for manual uploads
const handleImageFileChange = (event) => {
  const files = event.target.files;
  if (files.length > 0) {
    handleImageFiles(files);
  }
};

// Handle image files
const handleImageFiles = (files) => {
  if (files && files[0]) {
    const file = files[0];
    if (/^image\//.test(file.type)) {
      hadith.value.image = URL.createObjectURL(file); // Generate and store the image preview URL
      selectedImageFile.value = file;
    }
  }
};

/// AUDIO UPLOAD HANDLERS ///

// DropZone setup for Audio
const { isOverDropZone: isOverAudioDropZone } = useDropZone(dropZoneAudioRef, {
  onDrop: (files) => {
    handleAudioFiles(files);
  },
  onDragEnter: () => {},
  onDragLeave: () => {},
  dataTransferTypes: ['Files'],
  preventDefault: true,
});

// Function to trigger audio file input for uploads
const triggerAudioFileInput = () => {
  audioFileInputRef.value.click();
};

// Handle audio file input change for manual uploads
const handleAudioFileChange = (event) => {
  const files = event.target.files;
  if (files.length > 0) {
    handleAudioFiles(files);
  }
};

// Handle audio files
const handleAudioFiles = (files) => {
  if (files && files[0]) {
    const file = files[0];
    if (file.type === 'audio/mp3' || file.name.endsWith('.mp3')) {
      hadith.value.audio = file;
      hadith.value.audioUrl = URL.createObjectURL(file); // Generate and store the audio preview URL
      selectedAudioFile.value = file;
    } else {
      alert('Please upload an MP3 file.');
    }
  }
};

// Watch for changes in the hadith title, image, or audio to enable/disable the save button
watch([() => hadith.value.title, () => selectedImageFile.value, () => selectedAudioFile.value], () => {
  // Additional validations can be performed here if needed
});

// Confirm action
const confirm = () => {
  emit('confirm', {});
};

// Save action
const save = async () => {
  if (!hadith.value.title || !selectedImageFile.value || !selectedAudioFile.value) {
    alert('Please provide a title, select an image, and upload an MP3 file.');
    return;
  }

  try {
    // Create the hadith without files
    const hadithData = {
      title: hadith.value.title,
      author_id: hadith.value.author_id,
      // Include other hadith fields if necessary
    };

    const newHadith = await hadithStore.addHadith(hadithData);

    // Now upload the image file
    await hadithStore.uploadHadithImage(newHadith.id, selectedImageFile.value);

    // Now upload the MP3 file
    await hadithStore.uploadHadithMp3(newHadith.id, selectedAudioFile.value);

    await pagesStore.attachHadithToPage(pagesStore.selectedPage.id, newHadith.id)

    // Emit events and reset form
    emit('created');
    emit('close');
    // Reset form data
    hadith.value.title = '';
    hadith.value.author_id = null;
    hadith.value.image = null;
    hadith.value.audio = null;
    hadith.value.audioUrl = null;
    selectedImageFile.value = null;
    selectedAudioFile.value = null;
  } catch (error) {
    console.error('Error saving hadith:', error);
    alert('Failed to save hadith. Please try again.');
    // Handle error (e.g., show error message)
  }
};
</script>

<style scoped>
.dropzone {
  transition: background-color 0.3s, border-color 0.3s;
}
</style>
